/**
 * Utility function to handle chunk loading errors with retry functionality
 * This is especially useful for NextUI components and other dynamically imported chunks
 */

// Track retry attempts for each chunk
const chunkRetryAttempts = new Map();
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Create a lazy-loaded component with chunk error retry functionality
 * @param {Function} importFunction - The dynamic import function
 * @param {string} chunkName - Name identifier for the chunk (for logging)
 * @param {number} maxRetries - Maximum number of retry attempts
 */
export const createLazyComponentWithRetry = (
  importFunction,
  chunkName = "unknown",
  maxRetries = MAX_RETRY_ATTEMPTS
) => {
  return () => {
    const retryCount = chunkRetryAttempts.get(chunkName) || 0;

    return importFunction().catch((error) => {
      // Check if this is a chunk loading error
      const isChunkLoadError =
        error.name === "ChunkLoadError" ||
        (error.message && error.message.includes("Loading chunk"));

      if (isChunkLoadError && retryCount < maxRetries) {
        console.warn(
          `Chunk loading failed for ${chunkName}, retry attempt ${
            retryCount + 1
          }/${maxRetries}`
        );

        // Increment retry count
        chunkRetryAttempts.set(chunkName, retryCount + 1);

        // Wait a bit before retrying (progressive backoff)
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);

        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(importFunction());
          }, delay);
        });
      }

      // If we've exhausted retries or it's not a chunk error, rethrow
      console.error(
        `Failed to load chunk ${chunkName} after ${retryCount} retries:`,
        error
      );

      // Create a new error with clear information for the ErrorBoundary
      const chunkError = new Error(
        `Failed to load ${chunkName} component after ${retryCount} retry attempts`
      );
      chunkError.name = "ChunkLoadError";
      chunkError.originalError = error;
      chunkError.chunkName = chunkName;
      chunkError.retryCount = retryCount;

      throw chunkError;
    });
  };
};

/**
 * Reset retry attempts for a specific chunk or all chunks
 * @param {string} chunkName - Specific chunk name to reset, or null for all
 */
export const resetChunkRetryAttempts = (chunkName = null) => {
  if (chunkName) {
    chunkRetryAttempts.delete(chunkName);
  } else {
    chunkRetryAttempts.clear();
  }
};

/**
 * Global error handler for unhandled chunk loading errors
 * Can be attached to window.addEventListener('error', handleChunkError)
 */
export const handleChunkError = (event) => {
  const error = event.error;

  if (
    error &&
    (error.name === "ChunkLoadError" ||
      (error.message && error.message.includes("Loading chunk")))
  ) {
    console.error("Unhandled chunk loading error detected:", error);

    // Just prevent the default browser error handling
    // Let React's ErrorBoundary handle the display
    event.preventDefault();
  }
};

/**
 * Initialize chunk error handling
 * Call this in your main App component or index.js
 */
export const initializeChunkErrorHandling = () => {
  // Listen for unhandled chunk loading errors
  window.addEventListener("error", handleChunkError);

  // Listen for unhandled promise rejections (chunk loading can fail as promises)
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    if (
      error &&
      (error.name === "ChunkLoadError" ||
        (error.message && error.message.includes("Loading chunk")))
    ) {
      console.error("Unhandled chunk loading promise rejection:", error);
      event.preventDefault();
    }
  });
};
